import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DatabaseModeContextType {
  isUsingDatabaseApproach: boolean;
  setIsUsingDatabaseApproach: (value: boolean) => void;
  toggleDatabaseApproach: () => void;
}

const DatabaseModeContext = createContext<DatabaseModeContextType | undefined>(undefined);

interface DatabaseModeProviderProps {
  children: ReactNode;
}

export const DatabaseModeProvider: React.FC<DatabaseModeProviderProps> = ({ children }) => {
  // Always default to true (database approach) for consistency
  const [isUsingDatabaseApproach, setIsUsingDatabaseApproachState] = useState(() => {
    // Check localStorage but default to true if not set
    const stored = localStorage.getItem('use_database_first');
    return stored === null ? true : stored === 'true';
  });

  // Update localStorage when state changes
  const setIsUsingDatabaseApproach = (value: boolean) => {
    setIsUsingDatabaseApproachState(value);
    localStorage.setItem('use_database_first', value.toString());
    
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new CustomEvent('databaseModeChanged', { detail: { value } }));
  };

  const toggleDatabaseApproach = () => {
    setIsUsingDatabaseApproach(!isUsingDatabaseApproach);
  };

  // Listen for storage changes from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'use_database_first' && e.newValue !== null) {
        const newValue = e.newValue === 'true';
        if (newValue !== isUsingDatabaseApproach) {
          setIsUsingDatabaseApproachState(newValue);
        }
      }
    };

    // Listen for custom events from same tab
    const handleCustomEvent = (e: CustomEvent) => {
      const newValue = e.detail.value;
      if (newValue !== isUsingDatabaseApproach) {
        setIsUsingDatabaseApproachState(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('databaseModeChanged', handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('databaseModeChanged', handleCustomEvent as EventListener);
    };
  }, [isUsingDatabaseApproach]);

  const value: DatabaseModeContextType = {
    isUsingDatabaseApproach,
    setIsUsingDatabaseApproach,
    toggleDatabaseApproach,
  };

  return (
    <DatabaseModeContext.Provider value={value}>
      {children}
    </DatabaseModeContext.Provider>
  );
};

export const useDatabaseMode = (): DatabaseModeContextType => {
  const context = useContext(DatabaseModeContext);
  if (context === undefined) {
    throw new Error('useDatabaseMode must be used within a DatabaseModeProvider');
  }
  return context;
};

// Convenience hook that always returns true for components that should always use database mode
export const useAlwaysDatabaseMode = () => {
  return {
    isUsingDatabaseApproach: true,
    setIsUsingDatabaseApproach: () => {}, // No-op
    toggleDatabaseApproach: () => {}, // No-op
  };
}; 